import { useEffect, useMemo } from 'react';
import { useQuery, gql } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Typography, makeStyles } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { SequenceResponse, SequenceStatus } from 'types/schema.type';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { HistogramSummary } from './histogram/HistogramSummary';
import { LoadingSpinner } from '../LoadingSpinner';
import { Warnings } from './Warnings';
import { UserInfo } from './UserInfo';
import { getSequenceAnalysisAnnotations } from './utils/getSequenceAnnotations';
import { getSequenceAnalysisEpisodes } from './utils/getSequenceEpisodes';
import { RateChart } from './charts/RateChart';
import { QtcIntervalChart } from './charts/QtcIntervalChart';
import { HrvTimeSeriesChart } from './charts/HrvTimeSeriesChart';
import { RespirationSummaryTable } from './RespirationSummaryTable';
import { ApneaScreeningTable } from './charts/ApneaScreeningTable';
import { HrvInterpretation } from './HrvInterpretation';
import { SectionInterpretation } from './SectionInterpretation';
import { SectionError } from './SectionError';
import { collectReportErrors, reportStatusOf } from './utils/reportSectionErrors';
import { RiskBadge } from './RiskBadge';
import { HistogramChart } from './histogram/HistogramChart';
import { getSequenceAnalysisAutomaticAnnotationsData } from './utils/getSequenceAutomaticAnnotationsData';
import { Summary } from './Summary';
import { ClusterChartList } from './cluster/ClusterChartList';
import { HealthIndicationTable } from './healthIndication/HealthIndicationTable';
import { getSequenceBeatSummaryData } from './utils/getSequenceBeatSummaryData';
import { getSequenceEctopicBeats } from './utils/getSequenceEctopicBeats';
import { EctopyChartList, EctopyType } from './charts/EctopyChartList';
import { getSummaryData } from './utils/getSummaryData';
import {
  computeRespirationSummary,
  respirationForSummaryTable,
} from '../../utilities/respirationSummary';
import { calculateAverageRate } from './utils/calculateAverageRate';
import { calculateAverageValues } from './utils/calculateAverageValues';
import { AFibTable, AFibChartList } from './afib';
import { BradycardiaTable, BradycardiaChartList } from './bradycardia';
import {
  bradycardiaExcerptRequest,
  selectBradycardiaChartEpisodes,
  withRebuiltExcerpts,
} from './bradycardia/bradycardiaChartPlan';
import { TachycardiaTable, TachycardiaChartList } from './tachycardia';
import { PauseEpisodesTable, PauseEpisodeChartList } from './pause';
import { ExtremesHeartRateChartList } from './extremesHeartRate';
import { getUnixTime } from './utils/getUnixTime';
import { ANALYSIS_FRAGMENT, EPISODES_AND_AFIBS_FRAGMENT } from './fragments';
import { getSequenceTitle } from './utils/getSequenceTitle';

const GET_SEQUENCE_TABLE_DATA = gql`
  ${ANALYSIS_FRAGMENT}

  query GetSequenceByIdTableData($sequenceId: String!) {
    getSequenceById(sequenceId: $sequenceId) {
      error
      success
      sequence {
        id
        recordingIds
        analysisId
        userId
        status
        startedAt
        endedAt
        user {
          id
          name
          surname
          dateOfBirth
          sex
          email
          weight
          height
        }
        analysis {
          ...AnalysisFragment
          clusterData {
            id
            annotationValues {
              annotationValue
              clusters {
                clusterNumber
                rpeakLocations
                isValid
              }
            }
          }
        }
        healthIndication {
          id
          type
          recordedAt
          duration_s
          content
        }
      }
    }
  }
`;

// project_management#561: the chart query runs once the table query has landed
// and carries only what the charts draw. The beats, rates and counts come from
// the table query (they used to be sent twice), and of each charted
// bradycardia episode only the drawn chunks are fetched (`excerptSlices`) —
// the whole excerpts were 138 MB of a 151 MB response on a 16 h sequence.
const GET_SEQUENCE_CHART_DATA = gql`
  ${EPISODES_AND_AFIBS_FRAGMENT}

  query GetSequenceByIdChartData(
    $sequenceId: String!
    $language: Language
    $bradycardiaExcerpts: [ExcerptSlicesRequest!]!
  ) {
    getSequenceById(sequenceId: $sequenceId) {
      error
      success
      sequence {
        id
        startedAt
        endedAt
        analysis {
          id
          ...EpisodesAndAfibsFragment
          bradycardiaExcerpts: excerptSlices(excerpts: $bradycardiaExcerpts) {
            startIndex
            endIndex
            length
            slices {
              from
              to
              ch1
            }
          }
          hrvTimeSeries {
            timestamps
            windowDurationSec
            stepSizeSec
            sdnn
            rmssd
            pnn50
            meanRr
            meanHrBpm
            nnCount
          }
          respiration {
            medianRrBrpm
            meanRrBrpm
            minRrBrpm
            maxRrBrpm
            coveragePercent
            windowsAnalysed
            windowsTotal
            meanConfidence
          }
          respirationTimeSeries {
            timestamps
            windowDurationSec
            stepSizeSec
            respirationRate
            confidence
            methodAgreement
          }
          apnea {
            assessable
            burdenMinPerH
            apneaMinutes
            analysedMinutes
            excludedMinutes
            afExcludedMinutes
            analysedHours
            flagged
            flagThresholdMinPerH
            meanProbability
            modelVersion
          }
          apneaTimeSeries {
            hourly {
              hourStartSec
              apneaMinutes
              analysedMinutes
            }
          }
          hrvInterpretation(language: $language)
          overallInterpretation(language: $language) { text generatedAt }
          afibInterpretation(language: $language) { text generatedAt }
          ectopyInterpretation(language: $language) { text generatedAt }
          bradycardiaInterpretation(language: $language) { text generatedAt }
          tachycardiaInterpretation(language: $language) { text generatedAt }
          qtcInterpretation(language: $language) { text generatedAt }
          riskAssessment(language: $language) { level summary keyFindings generatedAt }
          clusterData {
            id
            annotationValues {
              annotationValue
              clusters {
                clusterNumber
                rpeakLocations
                excerpts
                isValid
              }
            }
          }
          sequenceIntervals {
            qtcIntervals
            qtcIndexes
          }
          extremesByHeartRate {
            min
            minIndex
            minExcerpt
            max
            maxIndex
            maxExcerpt
          }
        }
      }
    }
  }
`;

type GetSequenceTableDataResponse = {
  getSequenceById: SequenceResponse;
};

type GetSequenceChartDataResponse = {
  getSequenceById: SequenceResponse;
};

export type HiddenSections = {
  hideUserInfo?: boolean;
  hideSummary?: boolean;
  hideHistogram?: boolean;
  hideRateChart?: boolean;
  hideHrvChart?: boolean;
  hideRespirationChart?: boolean;
  hideApneaChart?: boolean;
  hideHealthIndications?: boolean;
  hideAFib?: boolean;
  hideBradycardia?: boolean;
  hideTachycardia?: boolean;
  hidePauseEpisodes?: boolean;
  hideWarnings?: boolean;
  hideQtcChart?: boolean;
  hideExtremesHR?: boolean;
  hideEctopyCharts?: boolean;
  hideClusters?: boolean;
  hideHistogramSummary?: boolean;
  hideInterpretations?: boolean;
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonStart: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  buttonMark: {
    margin: theme.spacing(2),
  },
  container: {
    marginTop: theme.spacing(12),
  },
  banner: {
    width: 800,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  paper: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 650,
  },
  title: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(2),
  },
}));

type SequenceListItemProps = {
  language?: string;
};

export function SequenceListItem({ language = 'en' }: SequenceListItemProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { sequenceId = '' } = useParams<{ sequenceId: string }>();
  const [hiddenSectionsRaw] = useLocalStorage<HiddenSections>(`hiddenSections_${sequenceId}`, {});
  const hiddenSections = hiddenSectionsRaw || {};

  const {
    data: sequenceTableData,
    loading: sequenceTableDataLoading,
    error: sequenceTableDataError,
  } = useQuery<GetSequenceTableDataResponse, { sequenceId: string }>(GET_SEQUENCE_TABLE_DATA, {
    variables: {
      sequenceId,
    },
    errorPolicy: 'all',
  });

  const sequenceTable = sequenceTableData?.getSequenceById.sequence;

  // Derived from the table query once: the beat timeline, the 30 s average
  // rates and — project_management#561 — the bradycardia episodes the report
  // charts, which decide the signal the chart query asks for.
  const tableDerived = useMemo(() => {
    if (!sequenceTable?.analysis) return null;

    const annotationTimestamps = getSequenceAnalysisAnnotations(sequenceTable);
    const tableAverageRates = calculateAverageRate(
      annotationTimestamps,
      sequenceTable.analysis.rate || [],
    );
    const chartedBradycardia = selectBradycardiaChartEpisodes(
      getSequenceAnalysisEpisodes(sequenceTable, tableAverageRates).bradycardiaEpisodes,
    ).filter((ep) => Number.isInteger(ep.startIndex) && Number.isInteger(ep.endIndex));

    return {
      annotationTimestamps,
      averageRates: tableAverageRates,
      chartedBradycardia,
      bradycardiaExcerpts: chartedBradycardia.map((ep) =>
        bradycardiaExcerptRequest({
          startIndex: ep.startIndex as number,
          endIndex: ep.endIndex as number,
          lowestAvgBeatIndex: ep.lowestAvgBeatIndex,
        }),
      ),
    };
  }, [sequenceTable]);

  const {
    data: sequenceChartData,
    loading: sequenceChartDataLoading,
    error: sequenceChartDataError,
  } = useQuery<
    GetSequenceChartDataResponse,
    {
      sequenceId: string;
      language: string;
      bradycardiaExcerpts: NonNullable<typeof tableDerived>['bradycardiaExcerpts'];
    }
  >(GET_SEQUENCE_CHART_DATA, {
    variables: {
      sequenceId,
      language,
      bradycardiaExcerpts: tableDerived?.bradycardiaExcerpts ?? [],
    },
    errorPolicy: 'all',
    skip: !tableDerived,
  });

  // Until the chart query has answered (or failed), its sections are loading —
  // including the moment between the table landing and the chart query
  // starting, so `#pdf-ready` can never appear before the charts.
  const chartPending = sequenceChartDataLoading || (!sequenceChartData && !sequenceChartDataError);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const isDownloadRequest = localStorage.getItem('isDownloadRequest') === 'true';

    if (isDownloadRequest) {
      const interval = setInterval(() => {
        const pdfContent = document.getElementById('pdf-ready');

        if (pdfContent) {
          window.print();
          clearInterval(interval);
          localStorage.removeItem('isDownloadRequest');
        }
      }, 1000); // Check every second

      return () => clearInterval(interval);
    }
  }, []);

  if (sequenceTableDataLoading) {
    return <LoadingSpinner />;
  }

  const sequenceChart = sequenceChartData?.getSequenceById.sequence;

  // project_management#545: both queries use errorPolicy 'all', so a failed
  // field arrives as null + an entry in errors[]. Errors are attributed to the
  // section they belong to and rendered in place of it; only a report with no
  // sequence at all is fatal. `#pdf-ready` (below) still appears only after the
  // chart query settled, so the PDF generator prints the same partial page.
  const reportErrors = collectReportErrors(
    [
      ...(sequenceTableDataError?.graphQLErrors ?? []),
      ...(sequenceChartDataError?.graphQLErrors ?? []),
    ],
    {
      chartUnavailable:
        !sequenceChartDataLoading && !sequenceChart && sequenceChartDataError
          ? sequenceChartDataError.message
          : null,
    },
  );
  const sectionError = reportErrors.forSection;

  const fatalMessages: string[] = !sequenceTable
    ? [
      ...(sequenceTableDataError?.graphQLErrors.map(({ message }) => message) ?? []),
      ...(sequenceTableDataError && !sequenceTableDataError.graphQLErrors.length
        ? [sequenceTableDataError.message]
        : []),
      ...(sequenceTableData?.getSequenceById.error ? [sequenceTableData.getSequenceById.error] : []),
    ]
    : [];

  if (fatalMessages.length) {
    return (
      <div data-report-status="error" id="pdf-ready">
        <div>{t('report.loadFailed')}:</div>
        {fatalMessages.map((message, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <span key={i}>{message}</span>
        ))}
      </div>
    );
  }

  if (!sequenceTable || !sequenceTable?.analysis) {
    if (sequenceTable?.status === SequenceStatus.Analyzing) {
      return (
        <div id="pdf-ready">
          <p>ECG analysis in progress...</p>
          <p>Your report will be available once ready.</p>
        </div>
      );
    }

    return (
      <div id="pdf-ready">
        <p>Sequence has not been analyzed yet.</p>
        <p>You need to trigger sequence analysis manually by pressing analysis (last) button.</p>
      </div>
    );
  }

  if (!sequenceTable?.user) return <div id="pdf-ready">User not signed in</div>;

  const beatCount = sequenceTable.analysis.beatCount || 0;
  const { annotationTimestamps: sequenceAnnotationsTimestampsInSs, averageRates } =
    tableDerived as NonNullable<typeof tableDerived>;

  const qtcTimestamps = (sequenceChart?.analysis?.sequenceIntervals?.qtcIndexes || []).map((v) =>
    getUnixTime(sequenceTable.startedAt, v),
  );
  const averageQtcIntervals = calculateAverageValues(
    qtcTimestamps,
    sequenceChart?.analysis?.sequenceIntervals?.qtcIntervals || [],
  );

  // When the chart query's episode fields failed, fall back to the table query's
  // copy of them (same fields, without chart excerpts) so the tables still render.
  // #561: the chart query no longer repeats the beats and rates; they come from
  // the table query.
  const chartWithTableBeats = sequenceChart && {
    ...sequenceChart,
    analysis: sequenceChart.analysis && {
      ...sequenceChart.analysis,
      annotations: sequenceTable.analysis.annotations,
      rate: sequenceTable.analysis.rate,
    },
  };
  const episodesSource = sectionError('episodes') ? sequenceTable : chartWithTableBeats || sequenceTable;
  const { afibs, bradycardiaEpisodes, pauseEpisodes, tachycardiaEpisodes } = getSequenceAnalysisEpisodes(
    episodesSource,
    averageRates,
  );

  // #561: the charted episodes with their excerpts rebuilt from the fetched chunks.
  const bradycardiaChartEpisodes = withRebuiltExcerpts(
    (tableDerived as NonNullable<typeof tableDerived>).chartedBradycardia as any[],
    sectionError('episodes') ? null : sequenceChart?.analysis?.bradycardiaExcerpts,
  );

  const {
    ventricularDuplets,
    ventricularTriplets,
    ventricularRuns,
    supraVentricularDuplets,
    supraVentricularTriplets,
    supraVentricularRuns,
  } = getSequenceEctopicBeats(sectionError('ectopy') ? sequenceTable : sequenceChart);

  const { clusterData } = getSequenceAnalysisAutomaticAnnotationsData(sequenceChart);

  const {
    ectopicBeats,
    beatsTableData,
    ventricularTableData,
    ventricularDupletsTableData,
    ventricularTripletsTableData,
    ventricularRunsTableData,
    supraventricularTableData,
    supraVentricularDupletsTableData,
    supraVentricularTripletsTableData,
    supraVentricularRunsTableData,
  } = getSequenceBeatSummaryData(sequenceTable, averageRates);

  // project_management#549: the summary table shows the validated-range
  // median (≤ 18 br/min) with its own coverage — computed by the same
  // function as the respiration section below, so the two never disagree.
  const respirationReport = computeRespirationSummary(
    sequenceChart?.analysis?.respirationTimeSeries ?? null,
    sequenceTable.startedAt,
    sequenceChart?.analysis?.respiration ?? null,
  );

  const sequenceSummaryData = getSummaryData({
    beatCount,
    averageRates,
    ectopicBeats,
    averageQtcIntervals,
    pauseEpisodes,
    hrv: sequenceTable.analysis.hrv,
    respiration: respirationForSummaryTable(respirationReport),
    apnea: sequenceChart?.analysis?.apnea ?? null,
  });

  const rpeakIndexes = sequenceTable.analysis.annotations.map(
    (annotation) => annotation.sampleIndex,
  );

  const duration = sequenceTable.endedAt - sequenceTable.startedAt;

  const filteredAverageRates = averageRates.filter((rate): rate is number => rate !== null);

  const { title, subtitle } = getSequenceTitle({
    from: sequenceTable.startedAt,
    to: sequenceTable.endedAt,
    seconds: duration / 1000,
  });

  return (
    <div className={classes.root}>
      <Typography variant="h5" className={classes.title}>
        {title}
      </Typography>
      <Typography variant="h6" gutterBottom>
        {subtitle}
      </Typography>

      {reportErrors.hasErrors && (
        <Alert className={classes.banner} severity="warning">
          {t('report.partialBanner', {
            sections: [
              ...reportErrors.failedSections.map((section) => t(`report.sections.${section}`)),
              ...reportErrors.other,
            ].join(', '),
          })}
        </Alert>
      )}

      {!hiddenSections.hideUserInfo && <UserInfo user={sequenceTable?.user} />}
      {!hiddenSections.hideSummary && <Summary summary={sequenceSummaryData} />}

      {!hiddenSections.hideInterpretations && sectionError('interpretations') && (
        <SectionError message={sectionError('interpretations') as string} section="interpretations" />
      )}
      {!hiddenSections.hideInterpretations && !sectionError('interpretations') && (
        <>
          <RiskBadge
            riskAssessment={sequenceChart?.analysis?.riskAssessment}
            loading={chartPending}
          />
          <SectionInterpretation
            title={t('interpretation.overall')}
            interpretation={sequenceChart?.analysis?.overallInterpretation}
            loading={chartPending}
          />
        </>
      )}

      {!hiddenSections.hideHrvChart && sectionError('hrv') && (
        <SectionError message={sectionError('hrv') as string} section="hrv" />
      )}
      {!hiddenSections.hideHrvChart && !sectionError('hrv') && (chartPending || sequenceChart?.analysis?.hrvTimeSeries) && (
        <>
          <HrvTimeSeriesChart
            data={sequenceChart?.analysis?.hrvTimeSeries ?? null}
            startTime={sequenceTable.startedAt}
            loading={chartPending}
          />
          <HrvInterpretation
            interpretation={sequenceChart?.analysis?.hrvInterpretation}
            loading={chartPending}
          />
        </>
      )}

      {/*
        Rendered only once the data is known to exist, deliberately unlike the
        HRV block above. HRV is present for every analysed sequence, so gating
        on `loading` there resolves into a real chart. Respiration is absent for
        any sequence analysed before the feature shipped, and absent entirely
        while it is switched off -- so including `loading` would flash a
        "Respiration Rate" card on every report and then remove it, advertising
        a section that is not there.
      */}
      {!hiddenSections.hideRespirationChart && sectionError('respiration') && (
        <SectionError message={sectionError('respiration') as string} section="respiration" />
      )}
      {!hiddenSections.hideRespirationChart
        && sequenceChart?.analysis?.respirationTimeSeries && (
        <RespirationSummaryTable
          data={sequenceChart.analysis.respirationTimeSeries}
          startTime={sequenceTable.startedAt}
          summary={sequenceChart.analysis.respiration}
        />
      )}

      {/* Same rule as respiration: gated on the data, not on loading, so no phantom card. */}
      {!hiddenSections.hideApneaChart && sectionError('apnea') && (
        <SectionError message={sectionError('apnea') as string} section="apnea" />
      )}
      {!hiddenSections.hideApneaChart && sequenceChart?.analysis?.apnea && (
        <ApneaScreeningTable
          data={sequenceChart.analysis.apneaTimeSeries ?? null}
          summary={sequenceChart.analysis.apnea}
          startTime={sequenceTable.startedAt}
        />
      )}

      {!hiddenSections.hideHistogram && <HistogramChart rate={filteredAverageRates} />}
      {!hiddenSections.hideRateChart && (
        <RateChart annotationTimestamps={sequenceAnnotationsTimestampsInSs} rate={averageRates} />
      )}

      {!hiddenSections.hideHealthIndications && (
        <HealthIndicationTable healthIndications={sequenceTable.healthIndication as any} />
      )}

      {sectionError('episodes') && (
        <SectionError message={sectionError('episodes') as string} section="episodes" />
      )}
      {!hiddenSections.hideAFib && <AFibTable afibs={afibs} />}
      {!hiddenSections.hideAFib && (
        <AFibChartList
          episodes={afibs}
          rpeaks={rpeakIndexes}
          isLoading={chartPending}
        />
      )}
      {!hiddenSections.hideAFib && !hiddenSections.hideInterpretations && (
        <SectionInterpretation
          title={t('interpretation.afib')}
          interpretation={sequenceChart?.analysis?.afibInterpretation}
          loading={chartPending}
        />
      )}

      {!hiddenSections.hideBradycardia && (
        <BradycardiaTable bradycardiaEpisodes={bradycardiaEpisodes} sequenceDurationMs={duration} />
      )}
      {!hiddenSections.hideBradycardia && (
        <BradycardiaChartList
          episodes={bradycardiaChartEpisodes as any}
          rpeaks={rpeakIndexes}
          isLoading={chartPending}
        />
      )}
      {!hiddenSections.hideBradycardia && !hiddenSections.hideInterpretations && (
        <SectionInterpretation
          title={t('interpretation.bradycardia')}
          interpretation={sequenceChart?.analysis?.bradycardiaInterpretation}
          loading={chartPending}
        />
      )}

      {!hiddenSections.hideTachycardia && (
        <TachycardiaTable episodes={tachycardiaEpisodes} />
      )}
      {!hiddenSections.hideTachycardia && (
        <TachycardiaChartList
          episodes={tachycardiaEpisodes as any}
          rpeaks={rpeakIndexes}
          isLoading={chartPending}
        />
      )}
      {!hiddenSections.hideTachycardia && !hiddenSections.hideInterpretations && (
        <SectionInterpretation
          title={t('interpretation.tachycardia')}
          interpretation={sequenceChart?.analysis?.tachycardiaInterpretation}
          loading={chartPending}
        />
      )}

      {!hiddenSections.hidePauseEpisodes && <PauseEpisodesTable pauseEpisodes={pauseEpisodes} />}
      {!hiddenSections.hidePauseEpisodes && (
        <PauseEpisodeChartList
          episodes={pauseEpisodes as any}
          rpeaks={rpeakIndexes}
          isLoading={chartPending}
        />
      )}

      {!hiddenSections.hideHistogramSummary && (
        <HistogramSummary
          beats={beatsTableData}
          ventricular={ventricularTableData}
          ventricularDuplets={ventricularDupletsTableData}
          ventricularTriplets={ventricularTripletsTableData}
          ventricularRuns={ventricularRunsTableData}
          supraVentricular={supraventricularTableData}
          supraVentricularDuplets={supraVentricularDupletsTableData}
          supraVentricularTriplets={supraVentricularTripletsTableData}
          supraVentricularRuns={supraVentricularRunsTableData}
        />
      )}

      {/* Remaining Charts */}
      {chartPending ? (
        <LoadingSpinner />
      ) : (
        <div data-report-status={reportStatusOf(reportErrors)} id="pdf-ready">
          {!hiddenSections.hideQtcChart && sectionError('qtc') && (
            <SectionError message={sectionError('qtc') as string} section="qtc" />
          )}
          {!hiddenSections.hideQtcChart && !sectionError('qtc') && (
            <QtcIntervalChart
              qtcIntervals={averageQtcIntervals}
              annotationTimestamps={qtcTimestamps}
            />
          )}
          {!hiddenSections.hideQtcChart && !hiddenSections.hideInterpretations && (
            <SectionInterpretation
              title={t('interpretation.qtc')}
              interpretation={sequenceChart?.analysis?.qtcInterpretation}
              loading={false}
            />
          )}
          {!hiddenSections.hideExtremesHR && sectionError('extremesHR') && (
            <SectionError message={sectionError('extremesHR') as string} section="extremesHR" />
          )}
          {!hiddenSections.hideExtremesHR && !sectionError('extremesHR') && (
            <ExtremesHeartRateChartList
              recordingStartedAt={sequenceTable.startedAt}
              extremes={sequenceChart?.analysis?.extremesByHeartRate as any}
              rpeaks={rpeakIndexes}
            />
          )}
          {!hiddenSections.hideEctopyCharts && sectionError('ectopy') && (
            <SectionError message={sectionError('ectopy') as string} section="ectopy" />
          )}
          {!hiddenSections.hideEctopyCharts && (
            <>
              <EctopyChartList title={EctopyType.VentricularDuplet} beats={ventricularDuplets} />
              <EctopyChartList title={EctopyType.VentricularTriplet} beats={ventricularTriplets} />
              <EctopyChartList title={EctopyType.VentricularRun} beats={ventricularRuns} />
              <EctopyChartList
                title={EctopyType.SupraVentricularDuplet}
                beats={supraVentricularDuplets}
              />
              <EctopyChartList
                title={EctopyType.SupraVentricularTriplet}
                beats={supraVentricularTriplets}
              />
              <EctopyChartList
                title={EctopyType.SupraVentricularRun}
                beats={supraVentricularRuns}
              />
            </>
          )}
          {!hiddenSections.hideEctopyCharts && !hiddenSections.hideInterpretations && (
            <SectionInterpretation
              title={t('interpretation.ectopy')}
              interpretation={sequenceChart?.analysis?.ectopyInterpretation}
              loading={false}
            />
          )}
          {!hiddenSections.hideClusters && sectionError('clusters') && (
            <SectionError message={sectionError('clusters') as string} section="clusters" />
          )}
          {!hiddenSections.hideClusters && !sectionError('clusters') && (
            <ClusterChartList analysisId={sequenceTable.analysis.id} annotations={clusterData} />
          )}
        </div>
      )}

      {!hiddenSections.hideWarnings && <Warnings />}
    </div>
  );
}
